/* eslint-disable max-lines */
import {
  RequirementWorkflowType,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { type IllustrationType } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { useGetRunBackgroundCheckOnBookingConfiguration } from "@src/appV2/redesign/BackgroundChecks/useGetRunBackgroundCheckOnBookingConfiguration";
import { Button } from "@src/appV2/redesign/components/Button";
import { type WorkerRequirement } from "@src/appV2/redesign/Documents/api/types";
import { DocumentStatusPill } from "@src/appV2/redesign/Documents/components/StatusPill";
import { getCheckrRequirementStatus } from "@src/appV2/redesign/Documents/Details/Workflows/Checkr/getCheckrRequirementStatus";
import { useGetCheckrApplicationStatus } from "@src/appV2/redesign/Documents/Details/Workflows/Checkr/useGetCheckrApplicationStatus";
import { DOCUMENT_DETAILS_FULL_PATH } from "@src/appV2/redesign/Documents/paths";
import { isRequirementInstantReview } from "@src/appV2/redesign/Documents/utils/isRequirementInstantReview";
import { getCustomDocumentStatusCardContent } from "@src/appV2/redesign/Documents/utils/workerRequirementStatusContent";
import { SHIFT_DISCOVERY_SHIFT_HELLO_SIGN_CONTAINER_MODAL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import { parseISO } from "date-fns";
import { generatePath, useHistory } from "react-router-dom";

import { useDefinedWorker } from "../../../../Worker/useDefinedWorker";
// eslint-disable-next-line unicorn/prevent-abbreviations
import { usePandaDocConfig } from "../../../Documents/Details/Workflows/PandaDoc/usePandaDocConfig";
// eslint-disable-next-line unicorn/prevent-abbreviations
import { usePandaDocSigningEnabled } from "../../../Documents/Details/Workflows/PandaDoc/usePandaDocSigningEnabled";
import { getIncompleteRequirementPrerequisiteActions } from "../../../Documents/utils/getIncompleteRequirementPrerequisiteActions";
import { QuickSignPill } from "../../Bookability/Documents/InstantReview/QuickSignPill";
import { ShiftBottomSheetBookabilityCriteriaCard } from "../BookabilityCriteriaCard";
import {
  DOCUMENT_BEHAVIOR_TYPES,
  type DocumentBehaviorType,
  WORKER_REQUIREMENT_STATUS_TO_PILL_MAP,
} from "./types";

export interface ShiftBottomSheetBlockingRequirementsCardProps {
  blockingRequirement: WorkerRequirement;
  shiftId: string;
  workplaceId: string;
  onNavigateToDocDetails?: (path: string) => void;
}

export function ShiftBottomSheetBlockingRequirementsCard(
  props: ShiftBottomSheetBlockingRequirementsCardProps
) {
  const { blockingRequirement, shiftId, workplaceId, onNavigateToDocDetails } = props;
  const worker = useDefinedWorker();

  const history = useHistory();
  const { navigateToModal } = useShiftModalsDataContext();

  const { isEnabled: isRunBackgroundCheckOnBookingEnabled } =
    useGetRunBackgroundCheckOnBookingConfiguration();

  const isCheckrBackgroundCheck =
    blockingRequirement.requirement?.attributes.requirementWorkflowType ===
    RequirementWorkflowType.CHECKR_BACKGROUND_CHECK;

  const { data: checkrApplicationStatus } = useGetCheckrApplicationStatus({
    enabled: isRunBackgroundCheckOnBookingEnabled && isCheckrBackgroundCheck,
  });

  const blockingRequirementStatus = isCheckrBackgroundCheck
    ? getCheckrRequirementStatus({
        status: blockingRequirement.status,
        checkrApplicationStatus: checkrApplicationStatus?.data,
        isRunBackgroundCheckOnBookingEnabled,
        isValid: blockingRequirement.isValid,
      })
    : blockingRequirement.status;

  // eslint-disable-next-line unicorn/prevent-abbreviations
  const isPandaDocSigningEnabled = usePandaDocSigningEnabled(blockingRequirement.requirementId);
  // eslint-disable-next-line unicorn/prevent-abbreviations
  const { redirectToSupport, useFallbackUrl } = usePandaDocConfig();

  const isInstantReview =
    isPandaDocSigningEnabled && (redirectToSupport || useFallbackUrl)
      ? false
      : isRequirementInstantReview(blockingRequirement);

  // Determine document behavior type once to avoid repetitive conditionals
  const documentBehaviorType: DocumentBehaviorType = (() => {
    if (!blockingRequirement.requirement?.attributes.visibleToHCP) {
      return DOCUMENT_BEHAVIOR_TYPES.INVISIBLE;
    }

    return DOCUMENT_BEHAVIOR_TYPES.VISIBLE;
  })();

  function getPill() {
    if (documentBehaviorType === DOCUMENT_BEHAVIOR_TYPES.INVISIBLE) {
      return <DocumentStatusPill variant="in_review" size="medium" />;
    }

    if (
      (isInstantReview && blockingRequirementStatus !== WorkerRequirementStatus.IN_REVIEW) ||
      blockingRequirement?.requirement?.attributes.requirementWorkflowType ===
        RequirementWorkflowType.TEN99_POLICY_COVERAGE
    ) {
      return <QuickSignPill />;
    }

    const variant = WORKER_REQUIREMENT_STATUS_TO_PILL_MAP[blockingRequirementStatus];

    if (isDefined(variant)) {
      const { pillLabel } = getCustomDocumentStatusCardContent({
        isRunBackgroundCheckOnBookingEnabled,
        requirementWorkflowType:
          blockingRequirement.requirement?.attributes.requirementWorkflowType,
        status: blockingRequirementStatus,
        resubmissionDisabledFor:
          blockingRequirement.requirement?.attributes.resubmissionDisabledFor,
      });

      return (
        <DocumentStatusPill
          variant={variant}
          size="medium"
          expiry={
            isDefined(blockingRequirement.expiry) ? parseISO(blockingRequirement.expiry) : undefined
          }
          label={pillLabel}
        />
      );
    }

    return undefined;
  }

  function getActionText() {
    const { requirementWorkflowType, resubmissionDisabledFor } =
      blockingRequirement.requirement?.attributes ?? {};

    if (requirementWorkflowType === RequirementWorkflowType.DOCUMENT_UPLOAD) {
      return "Upload";
    }

    const isResubmissionDisabled =
      resubmissionDisabledFor?.statuses?.includes(blockingRequirementStatus);
    if (
      requirementWorkflowType === RequirementWorkflowType.SIGN_WITH_HELLOSIGN &&
      !isResubmissionDisabled
    ) {
      return "Sign";
    }

    if (requirementWorkflowType === RequirementWorkflowType.TEN99_POLICY_COVERAGE) {
      return "Complete";
    }

    return "Review";
  }

  function getButtonText() {
    if (documentBehaviorType === DOCUMENT_BEHAVIOR_TYPES.INVISIBLE) {
      return "Contact Support";
    }

    if (blockingRequirementStatus === WorkerRequirementStatus.IN_REVIEW) {
      return "View Details";
    }

    return getActionText();
  }

  function getIcon(): IllustrationType {
    if (blockingRequirementStatus === WorkerRequirementStatus.IN_REVIEW) {
      return "document-review";
    }

    if (documentBehaviorType === DOCUMENT_BEHAVIOR_TYPES.INVISIBLE) {
      return "document-review";
    }

    if (isInstantReview) {
      return "quiz-required";
    }

    return "document-upload";
  }

  function getDescription() {
    if (documentBehaviorType === DOCUMENT_BEHAVIOR_TYPES.INVISIBLE) {
      return "We're processing this document on your behalf.";
    }
    const document = blockingRequirement.documents?.[0];
    const { requirementWorkflowType, resubmissionDisabledFor } =
      blockingRequirement.requirement?.attributes ?? {};
    const { statusMessage } = getCustomDocumentStatusCardContent({
      isRunBackgroundCheckOnBookingEnabled,
      requirementWorkflowType,
      status: blockingRequirementStatus,
      resubmissionDisabledFor,
      notification: document?.attributes?.notification ?? undefined,
    });

    if (statusMessage) {
      return statusMessage;
    }

    if (isInstantReview) {
      return "The document will be reviewed instantly.";
    }

    if (blockingRequirementStatus === WorkerRequirementStatus.IN_REVIEW) {
      return "The document is currently being reviewed by our team.";
    }

    if (blockingRequirementStatus === WorkerRequirementStatus.EXPIRING) {
      return "This document will expire before the shift date. Please complete it again.";
    }

    if (blockingRequirementStatus === WorkerRequirementStatus.REJECTED) {
      if (isCheckrBackgroundCheck && isRunBackgroundCheckOnBookingEnabled) {
        return "Your background check couldn't be approved. Please review.";
      }

      return "Your document was rejected. Please review and resubmit.";
    }

    if (blockingRequirementStatus === WorkerRequirementStatus.EXPIRED) {
      return "Your document has expired. Please review and resubmit.";
    }

    if (
      blockingRequirement.requirement?.attributes.requirementWorkflowType ===
      RequirementWorkflowType.TEN99_POLICY_COVERAGE
    ) {
      return "Get covered in a few minutes.";
    }

    return `Please review this document before booking this shift.`;
  }

  function getTitle() {
    if (
      blockingRequirement.requirement?.attributes.requirementWorkflowType ===
      RequirementWorkflowType.TEN99_POLICY_COVERAGE
    ) {
      return "This workplace requires Workers' Compensation coverage";
    }

    return blockingRequirement.requirement?.attributes.name;
  }

  const onClick = async () => {
    if (documentBehaviorType === DOCUMENT_BEHAVIOR_TYPES.INVISIBLE) {
      await openZendeskMessaging();
      return;
    }

    const isPrerequisiteActionRequired =
      getIncompleteRequirementPrerequisiteActions({
        worker,
        workerRequirement: blockingRequirement,
      }).length > 0;

    if (!isPrerequisiteActionRequired && isInstantReview) {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_HELLO_SIGN_CONTAINER_MODAL_PATH, {
        pathParams: {
          requirementId: blockingRequirement.requirementId,
          shiftId,
        },
        forceReplacePath: true,
      });
      return;
    }

    const documentDetailsPath = generatePath(DOCUMENT_DETAILS_FULL_PATH, {
      requirementId: blockingRequirement.requirementId,
    });

    const fullPath = `${documentDetailsPath}?workplaceId=${encodeURIComponent(workplaceId)}`;

    if (onNavigateToDocDetails) {
      onNavigateToDocDetails(fullPath);
    } else {
      history.push(fullPath);
    }
  };

  return (
    <ShiftBottomSheetBookabilityCriteriaCard
      title={getTitle()}
      description={getDescription()}
      illustrationType={getIcon()}
      pill={getPill()}
      footer={
        <Button
          fullWidth
          variant={
            blockingRequirementStatus === WorkerRequirementStatus.IN_REVIEW
              ? "outlined"
              : "contained"
          }
          size="medium"
          onClick={onClick}
        >
          {getButtonText()}
        </Button>
      }
    />
  );
}
/* eslint-enable max-lines */
